import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';
import { Footer } from '@components/molecules/Footer';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { useData } from '@hooks/useData';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { ResultsContainer } from '@components/organisms/ResultsContainer';
import { ResultsType } from '@types';
import { FilterSidebarPointsServices } from '@components/molecules/filters/FilterSidebarPointsServices';
import { GetPointsServicesSuccessResponse } from '@services/useGetPointsServices';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useGetUserPoints } from '@services/useGetUserPoints';

export function PointsServices() {
    const { navigate } = useCustomNavigate();
    const { isModuleActive } = useModule();
    const { request: requestUserPoints, loading } = useGetUserPoints();
    const { pointsServices, isLoadingPointsServices, allSettings, login, userPoints } = useData();
    const { results } = useFilters();
    const { pageToRequest, checkPageToRequest, observerRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('points-services-component.title'),
        nPoints: t('points-services-component.n-points', { amount: userPoints?.data?.points }),
        myPoints: t('points-services-component.my-points'),
    };

    // if gamification module is not active, user can't access this page
    useEffect(() => {
        if (allSettings && !isModuleActive(Modules.pointsGamification)) {
            navigate(`/${SITE_URLS.home}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings]);

    useEffect(() => {
        !loading && requestUserPoints();
    }, []);

    return (
        <>
            <StickyHeader />

            <BreadcrumbsBanner title={translations.title} links={[]} currentPage={translations.title} />

            <Space spaceBreakpoints={{ xs: { space: 50 } }} />

            <ResultsContainer
                type={ResultsType.pointsServices}
                results={(results as Exclude<GetPointsServicesSuccessResponse['data'], undefined>).map((item) => {
                    return {
                        uuid: item.uuid || '',
                        category: item.category || '',
                        name: item.name || '',
                        image: item.image?.src || '',
                        pointsCost: item.points_needed,
                    };
                })}
                scrollObserverRef={observerRef}
                filters={
                    <>
                        {login && (
                            <div className="bg-secondary py-4 px-3 mb-5 text-center">
                                <div className="d-flex align-items-center">
                                    <Typography color="primary" tagVariant={TypographyTagVariants.span} fontSize="24">
                                        <i className="fa-sharp fa-solid fa-trophy me-3"></i>
                                    </Typography>
                                    <Typography
                                        color="primary"
                                        fontSize="32"
                                        fontWeight="900"
                                        tagVariant={TypographyTagVariants.span}>
                                        <>{translations.nPoints}</>
                                    </Typography>
                                </div>
                                <Typography align="center">{translations.myPoints}</Typography>
                            </div>
                        )}
                        <FilterSidebarPointsServices pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                    </>
                }
                total={pointsServices?.pagination?.total || 0}
                order={{
                    options: [],
                    onChange: () => {},
                }}
                loading={isLoadingPointsServices}
            />

            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
}
